import { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Envelope, 
  Phone, 
  InstagramLogo, 
  FacebookLogo, 
  LinkedinLogo, 
  PinterestLogo,
  ArrowRight,
  CheckCircle
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Target date: April 15, 2026
const TARGET_DATE = new Date('2026-04-15T00:00:00').getTime();

const ComingSoon = () => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [error, setError] = useState('');
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  });

  // Countdown timer logic
  useEffect(() => {
    const calculateTimeLeft = () => {
      const now = new Date().getTime();
      const difference = TARGET_DATE - now;

      if (difference > 0) {
        setTimeLeft({
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
          minutes: Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60)),
          seconds: Math.floor((difference % (1000 * 60)) / 1000)
        });
      }
    };

    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);

    return () => clearInterval(timer);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!email || !email.includes('@')) {
      setError('Please enter a valid email address');
      return;
    }

    setIsSubmitting(true);

    try {
      await axios.post(`${API}/subscribe`, { email });
      setIsSubscribed(true);
      setEmail('');
    } catch (err) {
      if (err.response?.data?.detail) {
        setError(err.response.data.detail);
      } else {
        setError('Something went wrong. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatNumber = (num) => {
    return num.toString().padStart(2, '0');
  };

  return (
    <div className="coming-soon-page" data-testid="coming-soon-page">
      <div className="grid grid-cols-1 lg:grid-cols-2 min-h-screen">
        {/* Left Panel - Hero Image */}
        <div className="hero-image-panel hidden lg:block h-screen sticky top-0">
          <img 
            src="https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?q=80&w=2000"
            alt="Luxury home interior"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/40 via-transparent to-black/20" />
        </div>

        {/* Right Panel - Content */}
        <div className="content-panel min-h-screen lg:h-screen lg:overflow-auto px-8 py-12 md:px-16 md:py-16 lg:px-20 xl:px-24">
          <div className="flex flex-col justify-between h-full max-w-xl mx-auto lg:mx-0">
            
            {/* Top Section - Logo & Social */}
            <div className="flex justify-between items-start opacity-0 animate-fade-up">
              {/* Logo */}
              <div className="arteva-logo text-xl md:text-2xl" data-testid="logo">
                ARTEVA
              </div>

              {/* Social Icons */}
              <div className="flex gap-4" data-testid="social-links">
                <span className="social-icon cursor-pointer" aria-label="Instagram">
                  <InstagramLogo size={22} weight="light" />
                </span>
                <span className="social-icon cursor-pointer" aria-label="Facebook">
                  <FacebookLogo size={22} weight="light" />
                </span>
                <span className="social-icon cursor-pointer" aria-label="LinkedIn">
                  <LinkedinLogo size={22} weight="light" />
                </span>
                <span className="social-icon cursor-pointer" aria-label="Pinterest">
                  <PinterestLogo size={22} weight="light" />
                </span>
              </div>
            </div>

            {/* Middle Section - Main Content */}
            <div className="flex-1 flex flex-col justify-center py-12 lg:py-0">
              {/* Label */}
              <p className="text-xs font-medium tracking-[0.2em] uppercase text-neutral-400 mb-6 opacity-0 animate-fade-up animation-delay-100">
                Coming Soon
              </p>

              {/* Headline */}
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-light tracking-tight text-neutral-50 leading-[1.1] mb-6 opacity-0 animate-fade-up animation-delay-200" data-testid="headline">
                A New Standard<br />of Luxury
              </h1>

              {/* Subheadline */}
              <p className="font-body text-base md:text-lg font-light leading-relaxed text-neutral-400 mb-12 max-w-md opacity-0 animate-fade-up animation-delay-300" data-testid="subheadline">
                Bespoke luxury homes designed around your lifestyle, site, and aspirations — delivered with transparency and uncompromising quality.
              </p>

              {/* Countdown Timer */}
              <div className="countdown-timer mb-12 opacity-0 animate-fade-up animation-delay-400" data-testid="countdown-timer">
                <div className="countdown-unit">
                  <div className="countdown-number text-4xl sm:text-5xl lg:text-6xl">
                    {formatNumber(timeLeft.days)}
                  </div>
                  <div className="countdown-label text-[10px] sm:text-xs mt-2">Days</div>
                </div>
                
                <div className="countdown-divider hidden sm:block" />
                
                <div className="countdown-unit">
                  <div className="countdown-number text-4xl sm:text-5xl lg:text-6xl">
                    {formatNumber(timeLeft.hours)}
                  </div>
                  <div className="countdown-label text-[10px] sm:text-xs mt-2">Hours</div>
                </div>
                
                <div className="countdown-divider hidden sm:block" />
                
                <div className="countdown-unit">
                  <div className="countdown-number text-4xl sm:text-5xl lg:text-6xl">
                    {formatNumber(timeLeft.minutes)}
                  </div>
                  <div className="countdown-label text-[10px] sm:text-xs mt-2">Minutes</div>
                </div>
                
                <div className="countdown-divider hidden sm:block" />
                
                <div className="countdown-unit">
                  <div className="countdown-number text-4xl sm:text-5xl lg:text-6xl">
                    {formatNumber(timeLeft.seconds)}
                  </div>
                  <div className="countdown-label text-[10px] sm:text-xs mt-2">Seconds</div>
                </div>
              </div>

              {/* Email Signup Form */}
              <div className="opacity-0 animate-fade-up animation-delay-500">
                <p className="text-xs font-medium tracking-[0.2em] uppercase text-neutral-400 mb-4">
                  Be the first to know
                </p>

                {isSubscribed ? (
                  <div className="success-message flex items-center gap-3" data-testid="success-message">
                    <CheckCircle size={24} weight="light" />
                    <span className="text-base">Thank you. We'll be in touch soon.</span>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-4" data-testid="signup-form">
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Enter your email"
                      className="editorial-input flex-1 text-base"
                      data-testid="email-input"
                      disabled={isSubmitting}
                    />
                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      className="submit-btn px-6 py-3 rounded-none flex items-center justify-center gap-2"
                      data-testid="submit-button"
                    >
                      {isSubmitting ? 'Subscribing...' : (
                        <>
                          Notify Me
                          <ArrowRight size={16} weight="bold" />
                        </>
                      )}
                    </Button>
                  </form>
                )}

                {error && (
                  <p className="text-red-400 text-sm mt-3" data-testid="error-message">{error}</p>
                )}
              </div>
            </div>

            {/* Bottom Section - Contact Info */}
            <div className="pt-8 border-t border-neutral-800 opacity-0 animate-fade-up animation-delay-600" data-testid="contact-section">
              <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-8">
                <a 
                  href="mailto:contact@artevahomes.com.au" 
                  className="contact-link text-sm"
                  data-testid="email-link"
                >
                  <Envelope size={18} weight="light" />
                  contact@artevahomes.com.au
                </a>
                <a 
                  href="tel:0291887873" 
                  className="contact-link text-sm"
                  data-testid="phone-link"
                >
                  <Phone size={18} weight="light" />
                  02 9188 7873
                </a>
              </div>
              
              <p className="text-xs text-neutral-600 mt-6">
                © {new Date().getFullYear()} Arteva Homes. All rights reserved.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Hero Image - Shows at top on mobile */}
      <style>{`
        @media (max-width: 1023px) {
          .coming-soon-page {
            position: relative;
          }
          .coming-soon-page::before {
            content: '';
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background-image: url('https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?q=80&w=2000');
            background-size: cover;
            background-position: center;
            opacity: 0.15;
            z-index: -1;
          }
        }
      `}</style>
    </div>
  );
};

export default ComingSoon;
